/**
 * Archetype System
 * Tracks progress toward unlocking tactical operator archetypes
 */

import { db, type DBProgressionState } from './db';
import { loadProgressionState, saveProgressionState } from './progressionService';
import type { SessionLog } from './sessionStorage';
import { getAssessmentHistory } from './assessmentStorage';
import { computeSetVolume } from './volumeTracking';

/**
 * Get user's bodyweight from most recent assessment
 * Falls back to 180 lbs if no assessment found (reasonable default)
 */
export function getUserBodyweight(): number {
  try {
    const assessments = getAssessmentHistory();
    if (assessments.length > 0) {
      // Sort by date descending and find first with bodyweight
      const sorted = assessments.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      const withBW = sorted.find(a => a.bodyweight && a.bodyweight > 0);
      if (withBW?.bodyweight) {
        return withBW.bodyweight;
      }
    }
  } catch (error) {
    console.warn('Could not get user bodyweight from assessments:', error);
  }
  return 180; // Reasonable default
}

// ============================================================================
// ARCHETYPE DEFINITIONS
// ============================================================================

export interface ArchetypeDefinition {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  image: string;
  /** Per-tier images keyed by ArchetypeTier. Falls back to `image` when not set. */
  tierImages?: Partial<Record<ArchetypeTier, string>>;
  requirements: ArchetypeRequirement[];
}

/** Get the image for an archetype at a specific tier, falling back to the default image */
export function getArchetypeImage(archetypeId: string, tier?: ArchetypeTier): string {
  const def = ARCHETYPES[archetypeId];
  if (!def) return '/operator.png';
  if (tier && def.tierImages?.[tier]) return def.tierImages[tier]!;
  return def.image;
}

/** Whether the archetype image is unlocked (requires specialist tier) */
export function isArchetypeImageUnlocked(tier?: ArchetypeTier): boolean {
  if (!tier) return false;
  const ordinal = TIER_ORDER.find(t => t.tier === tier)?.ordinal ?? -1;
  return ordinal >= 2; // specialist = 2, master = 3
}

export interface ArchetypeRequirement {
  label: string;
  description: string;
  getValue: (progress: any) => number;
  target: number;
  unit: string;
}

// ============================================================================
// ARCHETYPE TIER SYSTEM
// ============================================================================

export type ArchetypeTier = 'initiate' | 'qualified' | 'specialist' | 'master';

export interface TierDefinition {
  tier: ArchetypeTier;
  label: string;
  ordinal: number;
}

export interface ArchetypeTierThresholds {
  primaryMetric: string;
  primaryMetricKey: string;
  thresholds: [number, number, number, number]; // [initiate, qualified, specialist, master]
  titles: [string, string, string, string]; // title at each tier
}

export interface ArchetypeTierResult {
  currentTier: ArchetypeTier;
  tierOrdinal: number; // 0-3
  tierLabel: string; // e.g. "Specialist"
  tierTitle: string; // e.g. "Iron Mule"
  nextTier: ArchetypeTier | null;
  nextTierLabel: string | null;
  nextTierTitle: string | null;
  nextTierThreshold: number | null;
  currentValue: number;
  progressToNextTier: number; // 0-100
  isFullyUnlocked: boolean; // Specialist or above
  isMaster: boolean;
}

export const TIER_ORDER: TierDefinition[] = [
  { tier: 'initiate', label: 'Initiate', ordinal: 0 },
  { tier: 'qualified', label: 'Qualified', ordinal: 1 },
  { tier: 'specialist', label: 'Specialist', ordinal: 2 },
  { tier: 'master', label: 'Master', ordinal: 3 },
];

export const ARCHETYPE_TIER_THRESHOLDS: Record<string, ArchetypeTierThresholds> = {
  'pack-mule': {
    primaryMetric: 'Ruck Miles',
    primaryMetricKey: 'ruckMiles',
    thresholds: [25, 50, 100, 200],
    titles: ['Load Bearer', 'Pack Mule', 'Iron Mule', 'Atlas Carrier'],
  },
  'breacher': {
    primaryMetric: 'Lifetime Volume',
    primaryMetricKey: 'lifetimeVolume',
    thresholds: [100000, 300000, 500000, 750000],
    titles: ['Ram', 'Entry Man', 'Shock Breacher', 'Door Killer'],
  },
  'scout': {
    primaryMetric: 'Run Miles',
    primaryMetricKey: 'runMiles',
    thresholds: [50, 100, 200, 300],
    titles: ['Pathfinder', 'Recon Specialist', 'Long Walker', 'Ghost Scout'],
  },
  'assaulter': {
    primaryMetric: 'Total Pull-Ups',
    primaryMetricKey: 'totalPullUps',
    thresholds: [150, 300, 500, 750],
    titles: ['Climber', 'Assaulter', 'CQB Specialist', 'Urban Hunter'],
  },
  'operator': {
    primaryMetric: 'Total Sessions',
    primaryMetricKey: 'totalSessions',
    thresholds: [40, 80, 150, 250],
    titles: ['Operator', 'Tier One', 'Unit Leader', 'Mission Commander'],
  },
};

/**
 * Calculate the current tier for an archetype based on primary metric progress
 */
export function calculateArchetypeTier(
  archetypeId: string,
  state: DBProgressionState
): ArchetypeTierResult {
  const tierConfig = ARCHETYPE_TIER_THRESHOLDS[archetypeId];
  if (!tierConfig) {
    return {
      currentTier: 'initiate', tierOrdinal: 0, tierLabel: 'Initiate',
      tierTitle: 'Recruit', nextTier: 'qualified', nextTierLabel: 'Qualified',
      nextTierTitle: null, nextTierThreshold: 0, currentValue: 0,
      progressToNextTier: 0, isFullyUnlocked: false, isMaster: false,
    };
  }

  const progress = state.archetypeProgress[archetypeId as keyof typeof state.archetypeProgress];
  const currentValue = progress ? (progress as any)[tierConfig.primaryMetricKey] || 0 : 0;
  const [t0, t1, t2, t3] = tierConfig.thresholds;

  let tierOrdinal = 0;
  if (currentValue >= t3) tierOrdinal = 3;
  else if (currentValue >= t2) tierOrdinal = 2;
  else if (currentValue >= t1) tierOrdinal = 1;
  else if (currentValue >= t0) tierOrdinal = 0;
  else tierOrdinal = -1; // Below initiate threshold

  // Clamp to 0 minimum for display
  const effectiveOrdinal = Math.max(0, tierOrdinal);
  const currentTier = TIER_ORDER[effectiveOrdinal].tier;
  const tierLabel = TIER_ORDER[effectiveOrdinal].label;
  const tierTitle = tierConfig.titles[effectiveOrdinal];

  // Next tier info
  // When below initiate (tierOrdinal === -1), next tier IS initiate (ordinal 0)
  let nextOrdinal: number | null;
  if (tierOrdinal === -1) {
    nextOrdinal = 0; // Next target is Initiate
  } else if (effectiveOrdinal < 3) {
    nextOrdinal = effectiveOrdinal + 1;
  } else {
    nextOrdinal = null; // Already Master
  }
  const nextTier = nextOrdinal !== null ? TIER_ORDER[nextOrdinal].tier : null;
  const nextTierLabel = nextOrdinal !== null ? TIER_ORDER[nextOrdinal].label : null;
  const nextTierTitle = nextOrdinal !== null ? tierConfig.titles[nextOrdinal] : null;
  const nextTierThreshold = nextOrdinal !== null ? tierConfig.thresholds[nextOrdinal] : null;

  // Progress to next tier (0-100%)
  let progressToNextTier = 0;
  if (tierOrdinal === -1) {
    // Below initiate: progress toward initiate threshold
    progressToNextTier = t0 > 0 ? Math.min((currentValue / t0) * 100, 100) : 0;
  } else if (tierOrdinal < 3) {
    const currentThreshold = tierConfig.thresholds[tierOrdinal];
    const nextThreshold = tierConfig.thresholds[tierOrdinal + 1];
    const range = nextThreshold - currentThreshold;
    progressToNextTier = range > 0 ? Math.min(((currentValue - currentThreshold) / range) * 100, 100) : 100;
  } else {
    progressToNextTier = 100; // Master = maxed
  }

  return {
    currentTier,
    tierOrdinal: effectiveOrdinal,
    tierLabel,
    tierTitle,
    nextTier,
    nextTierLabel,
    nextTierTitle,
    nextTierThreshold,
    currentValue,
    progressToNextTier: Math.round(progressToNextTier),
    isFullyUnlocked: effectiveOrdinal >= 2, // Specialist+
    isMaster: effectiveOrdinal === 3,
  };
}

export const ARCHETYPES: Record<string, ArchetypeDefinition> = {
  'pack-mule': {
    id: 'pack-mule',
    name: 'Pack Mule',
    subtitle: 'The Logistics Specialist',
    description: 'Moves the gear, sustains the mission. Master of loaded endurance.',
    icon: '🪓',
    image: '/archetypes/packmule.png',
    requirements: [
      {
        label: 'Ruck 100 miles',
        description: 'Total ruck distance with 25+ lbs load',
        getValue: (p) => p.ruckMiles,
        target: 100,
        unit: 'mi',
      },
      {
        label: '50 ruck sessions',
        description: 'Complete 50 ruck training sessions',
        getValue: (p) => p.ruckSessions,
        target: 50,
        unit: '',
      },
      {
        label: '10mi with 50+ lbs',
        description: 'Longest ruck with 50+ lbs load',
        getValue: (p) => p.longestRuckWith50lbs,
        target: 10,
        unit: 'mi',
      },
      {
        label: '2,000 Endurance Intel',
        description: 'Earn 2,000 Intel from endurance training',
        getValue: (p) => p.enduranceIntel,
        target: 2000,
        unit: '',
      },
      {
        label: 'Operator Score 60+',
        description: 'Achieve Operator Score of 60 or higher',
        getValue: (p) => p.operatorScore,
        target: 60,
        unit: '',
      },
    ],
  },
  'breacher': {
    id: 'breacher',
    name: 'Breacher',
    subtitle: 'The Door Kicker',
    description: 'Explosive strength, first through the breach. Master of raw power.',
    icon: '',
    image: '/archetypes/breacher.png',
    requirements: [
      {
        label: '750,000 lbs volume',
        description: 'Accumulate 750,000 lbs lifetime strength volume',
        getValue: (p) => p.lifetimeVolume,
        target: 750000,
        unit: 'lbs',
      },
      {
        label: '4x bodyweight 1RM total',
        description: 'Achieve 1RM total (Squat + Bench + Deadlift) ≥ 4x bodyweight',
        getValue: (p) => p.oneRMTotal,
        get target() { return getUserBodyweight() * 4; }, // Dynamic based on actual bodyweight
        unit: 'lbs',
      },
      {
        label: '100 strength sessions',
        description: 'Complete 100 strength training sessions',
        getValue: (p) => p.strengthSessions,
        target: 100,
        unit: '',
      },
      {
        label: '2,000 Strength Intel',
        description: 'Earn 2,000 Intel from strength training',
        getValue: (p) => p.strengthIntel,
        target: 2000,
        unit: '',
      },
      {
        label: 'Operator Score 60+',
        description: 'Achieve Operator Score of 60 or higher',
        getValue: (p) => p.operatorScore,
        target: 60,
        unit: '',
      },
    ],
  },
  'scout': {
    id: 'scout',
    name: 'Scout',
    subtitle: 'The Pathfinder',
    description: 'Speed, reconnaissance, advance element. Master of ground coverage.',
    icon: '',
    image: '/archetypes/scout.png',
    requirements: [
      {
        label: 'Run 200 miles',
        description: 'Total run/walk distance (unloaded cardio)',
        getValue: (p) => p.runMiles,
        target: 200,
        unit: 'mi',
      },
      {
        label: '100 cardio sessions',
        description: 'Complete 100 cardio training sessions',
        getValue: (p) => p.cardioSessions,
        target: 100,
        unit: '',
      },
      {
        label: 'Sub-7:00 mile',
        description: 'Run a mile in under 7:00',
        getValue: (p) => p.fastestMile,
        target: 420, // 7:00 in seconds
        unit: 's',
      },
      {
        label: '2,000 Endurance Intel',
        description: 'Earn 2,000 Intel from endurance training',
        getValue: (p) => p.enduranceIntel,
        target: 2000,
        unit: '',
      },
      {
        label: 'Operator Score 60+',
        description: 'Achieve Operator Score of 60 or higher',
        getValue: (p) => p.operatorScore,
        target: 60,
        unit: '',
      },
    ],
  },
  'assaulter': {
    id: 'assaulter',
    name: 'Assaulter',
    subtitle: 'The Climber',
    description: 'Vertical assault, rope work, obstacle specialist. Master of bodyweight.',
    icon: '',
    image: '/archetypes/assaulter.png',
    requirements: [
      {
        label: '500 pull-ups',
        description: 'Total pull-ups across all sessions',
        getValue: (p) => p.totalPullUps,
        target: 500,
        unit: '',
      },
      {
        label: '1,000 push-ups',
        description: 'Total push-ups across all sessions',
        getValue: (p) => p.totalPushUps,
        target: 1000,
        unit: '',
      },
      {
        label: '100 bodyweight sessions',
        description: 'Complete 100 bodyweight training sessions',
        getValue: (p) => p.bodyweightSessions,
        target: 100,
        unit: '',
      },
      {
        label: '10 muscle-ups in one session',
        description: 'Complete 10 muscle-ups in a single session',
        getValue: (p) => p.maxMuscleUpsInSession,
        target: 10,
        unit: '',
      },
      {
        label: '2,000 Bodyweight Intel',
        description: 'Earn 2,000 Intel from bodyweight training',
        getValue: (p) => p.bodyweightIntel,
        target: 2000,
        unit: '',
      },
      {
        label: 'Operator Score 60+',
        description: 'Achieve Operator Score of 60 or higher',
        getValue: (p) => p.operatorScore,
        target: 60,
        unit: '',
      },
    ],
  },
  'operator': {
    id: 'operator',
    name: 'Operator',
    subtitle: 'The All-Rounder',
    description: 'Mission-ready, no weaknesses. Master of all domains.',
    icon: '',
    image: '/archetypes/operator.png',
    requirements: [
      {
        label: '1,500+ Intel in ALL 5 pillars',
        description: 'Minimum 1,500 Intel in Strength, Endurance, Bodyweight, Recovery, Consistency',
        getValue: (p) => p.minPillarIntel,
        target: 1500,
        unit: '',
      },
      {
        label: '150 total sessions',
        description: 'Complete 150 training sessions (any type)',
        getValue: (p) => p.totalSessions,
        target: 150,
        unit: '',
      },
      {
        label: '1 program complete',
        description: 'Complete at least 1 full training program',
        getValue: (p) => p.programsCompleted,
        target: 1,
        unit: '',
      },
      {
        label: 'Operator Score 70+',
        description: 'Achieve Operator Score of 70 or higher',
        getValue: (p) => p.operatorScore,
        target: 70,
        unit: '',
      },
      {
        label: '60 days readiness ≥70',
        description: 'Maintain average readiness ≥70 for 60 days',
        getValue: (p) => p.readinessDaysAbove70,
        target: 60,
        unit: 'd',
      },
    ],
  },
};

// ============================================================================
// PROGRESS TRACKING
// ============================================================================

/**
 * Update archetype progress after a session
 */
export async function updateArchetypeProgress(
  userId: string,
  session: SessionLog,
  operatorScore: number = 0
): Promise<{ unlocked: string[] }> {
  const state = await loadProgressionState(userId);
  const newlyUnlocked: string[] = [];

  // Update Pack Mule progress (ruck sessions)
  const exercises = 'exercises' in session ? session.exercises : [];
  if (session.sessionType === 'assessment' || exercises?.some(e => e.exerciseType === 'ruck')) {
    const ruckExercises = exercises?.filter(e => e.exerciseType === 'ruck') || [];
    let hasRuckWithDistance = false;
    ruckExercises.forEach(ex => {
      if (ex.actualDistance) {
        hasRuckWithDistance = true;
        const miles = ex.actualDistance / 1609.34; // meters to miles
        state.archetypeProgress['pack-mule'].ruckMiles += miles;

        // Check for 50+ lbs ruck
        const plateCarrierWeight = 'plateCarrierWeight' in ex ? (ex as any).plateCarrierWeight : 0;
        const totalLoad = (ex.actualLoad || 0) + (plateCarrierWeight || 0);
        if (totalLoad >= 50 && miles > state.archetypeProgress['pack-mule'].longestRuckWith50lbs) {
          state.archetypeProgress['pack-mule'].longestRuckWith50lbs = miles;
        }
      }
    });
    // Count once per session, not per exercise
    if (hasRuckWithDistance) {
      state.archetypeProgress['pack-mule'].ruckSessions += 1;
    }
  }

  // Update Breacher progress (strength sessions)
  if (session.sessionType === 'pt' || session.sessionType === 'assessment') {
    const strengthExercises = exercises?.filter(e => 
      e.exerciseType === 'weight' && 
      ['Squat', 'Bench', 'Deadlift', 'Press', 'Row', 'Clean', 'Snatch'].some(name => 
        e.exerciseName.toLowerCase().includes(name.toLowerCase())
      )
    ) || [];
    
    if (strengthExercises.length > 0) {
      state.archetypeProgress['breacher'].strengthSessions += 1;
      
      // Calculate session volume
      let sessionVolume = 0;
      strengthExercises.forEach(ex => {
        if (ex.sets) {
          ex.sets.forEach(set => {
            sessionVolume += computeSetVolume(set, ex.exerciseName);
          });
        }
      });
      state.archetypeProgress['breacher'].lifetimeVolume += sessionVolume;
    }
  }

  // Update Scout progress (cardio sessions)
  if (session.sessionType === 'assessment' || exercises?.some(e => e.exerciseType === 'cardio')) {
    const cardioExercises = exercises?.filter(e =>
      e.exerciseType === 'cardio' &&
      !e.exerciseName.toLowerCase().includes('ruck')
    ) || [];

    let hasCardioWithDistance = false;
    cardioExercises.forEach(ex => {
      if (ex.actualDistance) {
        hasCardioWithDistance = true;
        const miles = ex.actualDistance / 1609.34; // meters to miles
        state.archetypeProgress['scout'].runMiles += miles;

        // Check for fastest mile (if distance is ~1 mile and time is recorded)
        if (miles >= 0.9 && miles <= 1.1 && ex.actualTime) {
          const timeInSeconds = parseTimeToSeconds(ex.actualTime);
          if (timeInSeconds > 0 && timeInSeconds < state.archetypeProgress['scout'].fastestMile) {
            state.archetypeProgress['scout'].fastestMile = timeInSeconds;
          }
        }
      }
    });
    // Count once per session, not per exercise
    if (hasCardioWithDistance) {
      state.archetypeProgress['scout'].cardioSessions += 1;
    }
  }

  // Update Assaulter progress (bodyweight sessions)
  if (session.sessionType === 'pt' || session.sessionType === 'assessment') {
    const bodyweightExercises = exercises?.filter(e => 
      e.exerciseType === 'bodyweight'
    ) || [];
    
    if (bodyweightExercises.length > 0) {
      state.archetypeProgress['assaulter'].bodyweightSessions += 1;
      
      bodyweightExercises.forEach(ex => {
        const exName = ex.exerciseName.toLowerCase();
        
        // Count pull-ups/chin-ups
        if (exName.includes('pull') || exName.includes('chin')) {
          const totalReps = ex.sets?.reduce((sum, set) => sum + (set.reps || 0), 0) || 0;
          state.archetypeProgress['assaulter'].totalPullUps += totalReps;
        }
        
        // Count push-ups
        if (exName.includes('push')) {
          const totalReps = ex.sets?.reduce((sum, set) => sum + (set.reps || 0), 0) || 0;
          state.archetypeProgress['assaulter'].totalPushUps += totalReps;
        }
        
        // Count muscle-ups
        if (exName.includes('muscle') && exName.includes('up')) {
          const totalReps = ex.sets?.reduce((sum, set) => sum + (set.reps || 0), 0) || 0;
          if (totalReps > state.archetypeProgress['assaulter'].maxMuscleUpsInSession) {
            state.archetypeProgress['assaulter'].maxMuscleUpsInSession = totalReps;
          }
        }
      });
    }
  }

  // Update Operator progress (all sessions count)
  state.archetypeProgress['operator'].totalSessions += 1;

  // Update pillar intel references (from state.pillarIntel)
  state.archetypeProgress['pack-mule'].enduranceIntel = state.pillarIntel.endurance;
  state.archetypeProgress['breacher'].strengthIntel = state.pillarIntel.strength;
  state.archetypeProgress['scout'].enduranceIntel = state.pillarIntel.endurance;
  state.archetypeProgress['assaulter'].bodyweightIntel = state.pillarIntel.bodyweight;
  state.archetypeProgress['operator'].minPillarIntel = Math.min(
    state.pillarIntel.strength,
    state.pillarIntel.endurance,
    state.pillarIntel.bodyweight,
    state.pillarIntel.recovery,
    state.pillarIntel.consistency
  );

  // Update operator scores
  state.archetypeProgress['pack-mule'].operatorScore = operatorScore;
  state.archetypeProgress['breacher'].operatorScore = operatorScore;
  state.archetypeProgress['scout'].operatorScore = operatorScore;
  state.archetypeProgress['assaulter'].operatorScore = operatorScore;
  state.archetypeProgress['operator'].operatorScore = operatorScore;

  // Check for unlocks
  const unlocked = checkArchetypeUnlocks(state);
  newlyUnlocked.push(...unlocked);

  // Unlock tier titles based on current progress
  if (!state.unlockedTitles) state.unlockedTitles = [];
  for (const arcId of Object.keys(ARCHETYPE_TIER_THRESHOLDS)) {
    const tier = calculateArchetypeTier(arcId, state);
    // Unlock titles for all achieved tiers (current and below)
    for (let i = 0; i <= tier.tierOrdinal; i++) {
      const tierName = TIER_ORDER[i].tier;
      const titleId = `${arcId}-${tierName}`;
      if (!state.unlockedTitles.includes(titleId)) {
        state.unlockedTitles.push(titleId);
      }
    }
  }

  await saveProgressionState(state);

  return { unlocked: newlyUnlocked };
}

/**
 * Check if any archetypes should be unlocked
 */
function checkArchetypeUnlocks(state: DBProgressionState): string[] {
  const newlyUnlocked: string[] = [];

  // Pack Mule
  if (!state.archetypeProgress['pack-mule'].unlocked) {
    const pm = state.archetypeProgress['pack-mule'];
    if (
      pm.ruckMiles >= 100 &&
      pm.ruckSessions >= 50 &&
      pm.longestRuckWith50lbs >= 10 &&
      pm.enduranceIntel >= 2000 &&
      pm.operatorScore >= 60
    ) {
      state.archetypeProgress['pack-mule'].unlocked = true;
      state.unlockedArchetypes.push('pack-mule');
      newlyUnlocked.push('pack-mule');
      console.log('� ARCHETYPE UNLOCKED: Pack Mule');
    }
  }

  // Breacher
  if (!state.archetypeProgress['breacher'].unlocked) {
    const br = state.archetypeProgress['breacher'];
    // Get actual bodyweight from user assessments
    const bodyweight = getUserBodyweight();
    const targetOneRM = bodyweight * 4;
    
    if (
      br.lifetimeVolume >= 750000 &&
      br.oneRMTotal >= targetOneRM &&
      br.strengthSessions >= 100 &&
      br.strengthIntel >= 2000 &&
      br.operatorScore >= 60
    ) {
      state.archetypeProgress['breacher'].unlocked = true;
      state.unlockedArchetypes.push('breacher');
      newlyUnlocked.push('breacher');
      console.log('� ARCHETYPE UNLOCKED: Breacher');
    }
  }

  // Scout
  if (!state.archetypeProgress['scout'].unlocked) {
    const sc = state.archetypeProgress['scout'];
    if (
      sc.runMiles >= 200 &&
      sc.cardioSessions >= 100 &&
      sc.fastestMile <= 420 && // 7:00 or faster
      sc.enduranceIntel >= 2000 &&
      sc.operatorScore >= 60
    ) {
      state.archetypeProgress['scout'].unlocked = true;
      state.unlockedArchetypes.push('scout');
      newlyUnlocked.push('scout');
      console.log('� ARCHETYPE UNLOCKED: Scout');
    }
  }

  // Assaulter
  if (!state.archetypeProgress['assaulter'].unlocked) {
    const as = state.archetypeProgress['assaulter'];
    if (
      as.totalPullUps >= 500 &&
      as.totalPushUps >= 1000 &&
      as.bodyweightSessions >= 100 &&
      as.maxMuscleUpsInSession >= 10 &&
      as.bodyweightIntel >= 2000 &&
      as.operatorScore >= 60
    ) {
      state.archetypeProgress['assaulter'].unlocked = true;
      state.unlockedArchetypes.push('assaulter');
      newlyUnlocked.push('assaulter');
      console.log('� ARCHETYPE UNLOCKED: Assaulter');
    }
  }

  // Operator
  if (!state.archetypeProgress['operator'].unlocked) {
    const op = state.archetypeProgress['operator'];
    if (
      op.minPillarIntel >= 1500 &&
      op.totalSessions >= 150 &&
      op.programsCompleted >= 1 &&
      op.operatorScore >= 70 &&
      op.readinessDaysAbove70 >= 60
    ) {
      state.archetypeProgress['operator'].unlocked = true;
      state.unlockedArchetypes.push('operator');
      newlyUnlocked.push('operator');
      console.log('� ARCHETYPE UNLOCKED: Operator');
    }
  }

  return newlyUnlocked;
}

/**
 * Update 1RM total for Breacher archetype
 */
export async function updateBreacherOneRMTotal(userId: string): Promise<void> {
  const state = await loadProgressionState(userId);

  // Get 1RMs from Dexie
  const oneRMs = await db.oneRMs.where('userId').equals(userId).toArray();

  // Calculate total for Squat + Bench + Deadlift
  const squat = oneRMs.find(rm => rm.exerciseName.toLowerCase().includes('squat'))?.weight || 0;
  const bench = oneRMs.find(rm => rm.exerciseName.toLowerCase().includes('bench'))?.weight || 0;
  const deadlift = oneRMs.find(rm => rm.exerciseName.toLowerCase().includes('deadlift'))?.weight || 0;

  const total = squat + bench + deadlift;
  state.archetypeProgress['breacher'].oneRMTotal = total;

  console.log(` Breacher 1RM Total: Squat=${squat} + Bench=${bench} + Deadlift=${deadlift} = ${total} lbs`);

  // Check for unlock
  checkArchetypeUnlocks(state);

  await saveProgressionState(state);
}

/**
 * Increment programsCompleted for the Operator archetype and check for unlocks
 * Call this when a user finishes the last day of their program.
 */
export async function markProgramCompleted(userId: string): Promise<{ unlocked: string[] }> {
  const state = await loadProgressionState(userId);
  state.archetypeProgress['operator'].programsCompleted += 1;
  console.log(` Programs completed: ${state.archetypeProgress['operator'].programsCompleted}`);
  const unlocked = checkArchetypeUnlocks(state);
  await saveProgressionState(state);
  return { unlocked };
}

/**
 * Update operator score for all archetypes
 * Call this when loading archetype panel to ensure current score is shown
 */
export async function updateAllArchetypeOperatorScores(userId: string, operatorScore: number): Promise<void> {
  const state = await loadProgressionState(userId);

  // Update all archetypes with current operator score
  state.archetypeProgress['pack-mule'].operatorScore = operatorScore;
  state.archetypeProgress['breacher'].operatorScore = operatorScore;
  state.archetypeProgress['scout'].operatorScore = operatorScore;
  state.archetypeProgress['assaulter'].operatorScore = operatorScore;
  state.archetypeProgress['operator'].operatorScore = operatorScore;

  console.log(` Updated all archetype operator scores to: ${operatorScore}`);

  // Check for unlocks
  checkArchetypeUnlocks(state);

  await saveProgressionState(state);
}

/**
 * Sync tier title unlocks based on current archetype progress.
 * Call this on barracks page load so existing progress earns titles
 * even if no new session has been logged since the tier system was added.
 */
export async function syncArchetypeTierTitles(userId: string): Promise<void> {
  return syncArchetypeTierRewards(userId);
}

/**
 * Archetype tier reward schedule:
 *   Initiate  → Emblem
 *   Qualified → Calling Card
 *   Specialist → Avatar (+ image reveal on progress panel)
 *   Master    → Legendary Title
 *
 * Lower-tier titles are always unlocked progressively (all tiers up to current).
 */
export async function syncArchetypeTierRewards(userId: string): Promise<void> {
  const state = await loadProgressionState(userId);
  if (!state.unlockedTitles) state.unlockedTitles = [];
  if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
  if (!state.unlockedCallingCards) state.unlockedCallingCards = ['default'];
  if (!state.unlockedAvatars) state.unlockedAvatars = ['default'];

  let changed = false;

  for (const arcId of Object.keys(ARCHETYPE_TIER_THRESHOLDS)) {
    const tier = calculateArchetypeTier(arcId, state);

    // Unlock tier titles for all achieved tiers (initiate through current)
    for (let i = 0; i <= tier.tierOrdinal; i++) {
      const tierName = TIER_ORDER[i].tier;
      const titleId = `${arcId}-${tierName}`;
      if (!state.unlockedTitles.includes(titleId)) {
        state.unlockedTitles.push(titleId);
        changed = true;
      }
    }

    // Initiate (ordinal 0) → Emblem
    if (tier.tierOrdinal >= 0 && !state.unlockedEmblems.includes(arcId)) {
      state.unlockedEmblems.push(arcId);
      changed = true;
    }

    // Qualified (ordinal 1) → Calling Card
    if (tier.tierOrdinal >= 1 && !state.unlockedCallingCards.includes(arcId)) {
      state.unlockedCallingCards.push(arcId);
      changed = true;
    }

    // Specialist (ordinal 2) → Avatar
    if (tier.tierOrdinal >= 2 && !state.unlockedAvatars.includes(arcId)) {
      state.unlockedAvatars.push(arcId);
      changed = true;
    }

    // Master (ordinal 3) → legendary title already granted via tier title loop above
  }

  if (changed) {
    await saveProgressionState(state);
  }
}

/**
 * Calculate overall progress for an archetype (0-100%)
 */
export function calculateArchetypeProgress(
  archetypeId: string,
  state: DBProgressionState
): number {
  const archetype = ARCHETYPES[archetypeId];
  if (!archetype) return 0;

  const progress = state.archetypeProgress[archetypeId as keyof typeof state.archetypeProgress];
  if (!progress) return 0;

  const requirements = archetype.requirements;
  const totalProgress = requirements.reduce((sum, req) => {
    const current = req.getValue(progress);
    let percent = 0;
    
    // Special handling for "fastest mile" (lower is better)
    if (req.label.includes('Sub-7:00 mile')) {
      if (current >= 999999) {
        percent = 0; // No time recorded yet
      } else if (current <= req.target) {
        percent = 100; // Target achieved
      } else {
        // Partial credit: scale from 10:00 (600s) to 7:00 (420s)
        const maxTime = 600;
        const targetTime = req.target;
        percent = Math.max(0, ((maxTime - current) / (maxTime - targetTime)) * 100);
      }
    } else {
      percent = Math.min((current / req.target) * 100, 100);
    }
    
    return sum + percent;
  }, 0) / requirements.length;

  return Math.round(totalProgress);
}

/**
 * Get recommended archetype based on current progress
 */
export function getRecommendedArchetype(state: DBProgressionState): {
  archetypeId: string;
  name: string;
  progress: number;
  reasoning: string;
  tier: ArchetypeTierResult;
} | null {
  const archetypes = Object.keys(ARCHETYPES).map(id => ({
    id,
    name: ARCHETYPES[id].name,
    progress: calculateArchetypeProgress(id, state),
    unlocked: state.archetypeProgress[id as keyof typeof state.archetypeProgress]?.unlocked || false,
  }));

  // Filter out already unlocked
  const locked = archetypes.filter(a => !a.unlocked);

  if (locked.length === 0) {
    // All unlocked — recommend highest tier archetype for Master push
    const allWithTiers = archetypes.map(a => ({
      ...a,
      tier: calculateArchetypeTier(a.id, state),
    }));
    const nonMaster = allWithTiers.filter(a => !a.tier.isMaster);
    if (nonMaster.length === 0) return null; // All mastered
    nonMaster.sort((a, b) => b.tier.progressToNextTier - a.tier.progressToNextTier);
    const best = nonMaster[0];
    return {
      archetypeId: best.id,
      name: best.name,
      progress: best.progress,
      reasoning: generateReasoning(best.id, state),
      tier: best.tier,
    };
  }

  // Sort by progress (highest first)
  locked.sort((a, b) => b.progress - a.progress);

  const recommended = locked[0];
  return {
    archetypeId: recommended.id,
    name: recommended.name,
    progress: recommended.progress,
    reasoning: generateReasoning(recommended.id, state),
    tier: calculateArchetypeTier(recommended.id, state),
  };
}

// ============================================================================
// NEAR-MISS NUDGES
// ============================================================================

export interface ArchetypeNudge {
  archetypeId: string;
  archetypeName: string;
  overallProgress: number;
  message: string;
  urgency: 'low' | 'medium' | 'high';
  closestRequirement: {
    label: string;
    current: number;
    target: number;
    percentComplete: number;
  };
}

/**
 * Get near-miss nudges for archetypes user is close to unlocking
 * Returns nudges for archetypes at 60%+ progress
 */
export function getArchetypeNudges(state: DBProgressionState): ArchetypeNudge[] {
  const nudges: ArchetypeNudge[] = [];

  for (const [id, archetype] of Object.entries(ARCHETYPES)) {
    const progress = state.archetypeProgress[id as keyof typeof state.archetypeProgress];
    if (!progress || progress.unlocked) continue;

    const overallProgress = calculateArchetypeProgress(id, state);

    // Only nudge if 60%+ complete
    if (overallProgress < 60) continue;

    // Find the closest requirement to completion
    let closestReq = { label: '', current: 0, target: 0, percentComplete: 0 };
    let highestPercent = 0;

    for (const req of archetype.requirements) {
      const current = req.getValue(progress);
      let percent = 0;

      // Handle inverse requirements (like fastest mile)
      if (req.label.includes('Sub-7:00')) {
        if (current >= 999999) {
          percent = 0;
        } else if (current <= req.target) {
          percent = 100;
        } else {
          percent = Math.max(0, ((600 - current) / (600 - req.target)) * 100);
        }
      } else {
        percent = Math.min((current / req.target) * 100, 100);
      }

      if (percent > highestPercent && percent < 100) {
        highestPercent = percent;
        closestReq = {
          label: req.label,
          current,
          target: req.target,
          percentComplete: Math.round(percent),
        };
      }
    }

    // Generate urgency and message based on progress
    let urgency: 'low' | 'medium' | 'high' = 'low';
    let message = '';

    if (overallProgress >= 90) {
      urgency = 'high';
      message = `You're SO CLOSE to unlocking ${archetype.name}! Just ${100 - closestReq.percentComplete}% more on ${closestReq.label}.`;
    } else if (overallProgress >= 80) {
      urgency = 'medium';
      message = `${archetype.name} is within reach! Focus on ${closestReq.label} to get there.`;
    } else {
      urgency = 'low';
      message = `Keep pushing toward ${archetype.name}. You're ${overallProgress}% of the way there.`;
    }

    nudges.push({
      archetypeId: id,
      archetypeName: archetype.name,
      overallProgress,
      message,
      urgency,
      closestRequirement: closestReq,
    });
  }

  // Sort by progress (highest first) then by urgency
  nudges.sort((a, b) => {
    if (a.urgency !== b.urgency) {
      const urgencyOrder = { high: 3, medium: 2, low: 1 };
      return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
    }
    return b.overallProgress - a.overallProgress;
  });

  return nudges;
}

/**
 * Get a single high-priority nudge for display in UI
 */
export function getTopArchetypeNudge(state: DBProgressionState): ArchetypeNudge | null {
  const nudges = getArchetypeNudges(state);
  return nudges.length > 0 ? nudges[0] : null;
}

function generateReasoning(archetypeId: string, state: DBProgressionState): string {
  const tier = calculateArchetypeTier(archetypeId, state);
  const tierConfig = ARCHETYPE_TIER_THRESHOLDS[archetypeId];

  // Build tier-aware prefix
  const tierPrefix = `Currently ${tier.tierTitle} (${tier.tierLabel}).`;

  // Build next-tier suffix
  let tierSuffix = '';
  if (tier.nextTierTitle && tier.nextTierThreshold !== null) {
    const remaining = Math.max(0, tier.nextTierThreshold - tier.currentValue);
    const metricLabel = tierConfig?.primaryMetric?.toLowerCase() || 'progress';
    if (archetypeId === 'breacher') {
      tierSuffix = ` ${(remaining / 1000).toFixed(0)}K more lbs volume to reach ${tier.nextTierTitle} (${tier.nextTierLabel}).`;
    } else {
      tierSuffix = ` ${Math.ceil(remaining)} more ${metricLabel} to reach ${tier.nextTierTitle} (${tier.nextTierLabel}).`;
    }
  } else if (tier.isMaster) {
    tierSuffix = ' Maximum tier achieved.';
  }

  return `${tierPrefix}${tierSuffix}`;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function parseTimeToSeconds(timeString: string): number {
  // Parse "HH:MM:SS" or "MM:SS" to seconds
  const parts = timeString.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0; // Invalid time
}

